import * as AccordionPrimitive from '@radix-ui/react-accordion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceDateGroup } from '@/components/Dashboard/AbsenceDateGroup';
import { AbsenceDetail } from '@/components/Dashboard/AbsenceDetail';
import { TileModal } from '@/components/Dashboard/TileModal';
import { useEvaluationsSummary } from '@/components/Dashboard/hooks/useEvaluationsSummary';
import type { NonAttendance } from '@/types/overview';
import { formatMonth, groupByMonth, sortByDateDesc } from '@/utils/date';

export type AbsenceDetailSelection = {
  absence: NonAttendance;
  date: string;
};

interface Props {
  isOpen: boolean;
  selectedDetail: AbsenceDetailSelection | undefined;
  onClose: () => void;
  onBack?: () => void;
  onAbsenceClick: (absence: NonAttendance, date: string) => void;
}

export const AbsencesModal: React.FC<Props> = ({
  isOpen,
  selectedDetail,
  onClose,
  onBack,
  onAbsenceClick,
}) => {
  const { t } = useTranslation();
  const { data } = useEvaluationsSummary();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const sorted = sortByDateDesc(data?.nonAttendances ?? [], ({ date }) => date);
  const monthGroups = Array.from(
    groupByMonth(sorted, ({ date }) => date).values(),
  );
  const title = selectedDetail
    ? selectedDetail.absence.disciplineName
    : t('home.dashboard.absences.title');

  const monthEntries = monthGroups.map((dateGroups, monthIndex) => {
    const monthPrefix = `absences-${monthIndex}-`;
    const value = expandedGroups.filter((entry) =>
      entry.startsWith(monthPrefix),
    );

    const onValueChange = (next: string[]) =>
      setExpandedGroups((prev) => [
        ...prev.filter((entry) => !entry.startsWith(monthPrefix)),
        ...next,
      ]);

    return { monthIndex, monthPrefix, dateGroups, value, onValueChange };
  });

  const handleClose = () => {
    setExpandedGroups([]);
    onClose();
  };

  return (
    <TileModal
      isOpen={isOpen}
      title={title}
      onClose={handleClose}
      onBack={selectedDetail ? onBack : undefined}
    >
      {selectedDetail && (
        <AbsenceDetail nonAttendance={selectedDetail.absence} />
      )}
      {!selectedDetail && (
        <div className="flex flex-col gap-6 px-4 py-4 md:px-6 md:py-6">
          {monthEntries.map(
            ({ monthIndex, monthPrefix, dateGroups, value, onValueChange }) => (
              <section key={monthIndex} className="flex flex-col gap-3">
                <h3 className="text-headline-20 capitalize text-foreground">
                  {formatMonth(dateGroups[0].date)}
                </h3>
                <AccordionPrimitive.Root
                  type="multiple"
                  value={value}
                  onValueChange={onValueChange}
                  className="bg-card shadow-card flex flex-col rounded-xl"
                >
                  {dateGroups.map((group, groupIndex) => (
                    <AbsenceDateGroup
                      key={groupIndex}
                      group={group}
                      value={`${monthPrefix}${groupIndex}`}
                      onAbsenceClick={onAbsenceClick}
                    />
                  ))}
                </AccordionPrimitive.Root>
              </section>
            ),
          )}
        </div>
      )}
    </TileModal>
  );
};
